import { motion } from 'framer-motion';
import {
  CalendarDays,
  Users,
  ClipboardList,
  Briefcase,
  XCircle,
  Ticket,
  Wallet,
  Tickets,
  Receipt,
  PiggyBank,
  Euro,
  Coins,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import MetricCard, { Tono } from './MetricCard';
import DashboardSeccion from './DashboardSeccion';
import { DashboardStats } from './types';

interface Props {
  stats: DashboardStats;
  forzarAbiertas?: boolean;
}

interface Metrica {
  titulo: string;
  valor: number;
  icono: React.ReactNode;
  tono: Tono;
  ruta?: string;
  destino?: string;
  delta?: number | null;
  textoComparacion?: string;
  invertido?: boolean;
  etiquetaFija?: string;
  isMoney?: boolean;
  className: string;
  wrapperClassName?: string;
}

const BASE = '/panel-empresa';

const gridVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
};

const Seccion = ({
  id,
  titulo,
  grid,
  metricas,
  destacado = false,
  forzarAbierta,
  resumen,
  onNavigate,
  linkLabel,
}: {
  id: string;
  titulo: string;
  grid: string;
  metricas: Metrica[];
  destacado?: boolean;
  forzarAbierta?: boolean;
  resumen?: string;
  onNavigate: (ruta: string) => void;
  linkLabel: (destino: string) => string;
}) => (
  <DashboardSeccion id={id} titulo={titulo} forzarAbierta={forzarAbierta} resumen={resumen}>
    <motion.div
      variants={gridVariants}
      initial="hidden"
      animate="visible"
      className={`grid gap-3 sm:gap-4 ${grid}`}
    >
      {metricas.map(({ ruta, destino, ...m }) => (
        <MetricCard
          key={m.className}
          {...m}
          destacado={destacado}
          onClick={ruta ? () => onNavigate(ruta) : undefined}
          linkLabel={destino ? linkLabel(destino) : undefined}
        />
      ))}
    </motion.div>
  </DashboardSeccion>
);

const DashboardMetrics = ({ stats, forzarAbiertas = false }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const vsMes = t('dashboardMetrics.vsMesPasado');

  const finanzas: Metrica[] = [
    {
      titulo: t('dashboardMetrics.facturado'),
      valor: stats.facturadoMes,
      icono: <Euro />,
      tono: 'green',
      delta: stats.facturadoMesVsMesPasado,
      textoComparacion: vsMes,
      isMoney: true,
      className: 'tour-metric-facturadoMes',
    },
    {
      titulo: t('dashboardMetrics.gastos'),
      valor: stats.gastosMes,
      icono: <Receipt />,
      tono: 'red',
      ruta: `${BASE}/expenses`,
      destino: t('dashboardMetrics.destinos.expenses'),
      delta: stats.gastosMesVsMesPasado,
      textoComparacion: vsMes,
      invertido: true,
      isMoney: true,
      className: 'tour-metric-gastosMes',
    },
    {
      titulo: t('dashboardMetrics.beneficioNeto'),
      valor: stats.beneficioMes,
      icono: <PiggyBank />,
      tono: stats.beneficioMes >= 0 ? 'blue' : 'red',
      etiquetaFija:
        typeof stats.margenMes === 'number'
          ? `${t('dashboardMetrics.margen')} ${stats.margenMes.toLocaleString('es-ES', {
              maximumFractionDigits: 1,
            })}%`
          : undefined,
      isMoney: true,
      className: 'tour-metric-beneficioMes',
      wrapperClassName: 'col-span-2 sm:col-span-1',
    },
  ];

  const actividad: Metrica[] = [
    {
      titulo: t('dashboardMetrics.citasHoy'),
      valor: stats.citasHoy,
      icono: <CalendarDays />,
      tono: 'blue',
      ruta: `${BASE}/calendar`,
      destino: t('dashboardMetrics.destinos.calendar'),
      className: 'tour-metric-citasHoy',
    },
    {
      titulo: t('dashboardMetrics.citasMes'),
      valor: stats.citasMes,
      icono: <ClipboardList />,
      tono: 'blue',
      ruta: `${BASE}/appointments`,
      destino: t('dashboardMetrics.destinos.appointments'),
      delta: stats.citasMesVsMesPasado,
      textoComparacion: vsMes,
      className: 'tour-metric-citasMes',
    },
    {
      titulo: t('dashboardMetrics.cancelaciones'),
      valor: stats.cancelacionesMes,
      icono: <XCircle />,
      tono: 'red',
      ruta: `${BASE}/appointments`,
      destino: t('dashboardMetrics.destinos.appointments'),
      delta: stats.cancelacionesMesVsMesPasado,
      textoComparacion: vsMes,
      invertido: true,
      className: 'tour-metric-cancelacionesMes',
    },
    {
      titulo: t('dashboardMetrics.trabajadores'),
      valor: stats.trabajadores,
      icono: <Users />,
      tono: 'slate',
      ruta: `${BASE}/workers`,
      destino: t('dashboardMetrics.destinos.workers'),
      className: 'tour-metric-trabajadores',
    },
    {
      titulo: t('dashboardMetrics.servicios'),
      valor: stats.serviciosDisponibles,
      icono: <Briefcase />,
      tono: 'slate',
      ruta: `${BASE}/services`,
      destino: t('dashboardMetrics.destinos.services'),
      className: 'tour-metric-serviciosDisponibles',
      wrapperClassName: 'col-span-2 sm:col-span-1',
    },
  ];

  const bonos: Metrica[] = [
    {
      titulo: t('dashboardMetrics.bonosActivos'),
      valor: stats.bonosDisponibles,
      icono: <Ticket />,
      tono: 'violet',
      ruta: `${BASE}/bonus`,
      destino: t('dashboardMetrics.destinos.bonus'),
      className: 'tour-metric-bonosDisponibles',
    },
    {
      titulo: t('dashboardMetrics.bonosVendidos'),
      valor: stats.bonosVendidosMes,
      icono: <Tickets />,
      tono: 'violet',
      ruta: `${BASE}/purchasedVouchers`,
      destino: t('dashboardMetrics.destinos.purchasedVouchers'),
      className: 'tour-metric-bonosVendidos',
    },
    {
      titulo: t('dashboardMetrics.reservasBono'),
      valor: stats.usosBonosMes,
      icono: <Wallet />,
      tono: 'violet',
      ruta: `${BASE}/purchasedVouchers`,
      destino: t('dashboardMetrics.destinos.purchasedVouchers'),
      className: 'tour-metric-usosBonos',
    },
    {
      titulo: t('dashboardMetrics.ingresosBonos'),
      valor: stats.ingresosBonosMes,
      icono: <Coins />,
      tono: 'amber',
      ruta: `${BASE}/purchasedVouchers`,
      destino: t('dashboardMetrics.destinos.purchasedVouchers'),
      delta: stats.ingresosBonosMesVsMesPasado,
      textoComparacion: vsMes,
      isMoney: true,
      className: 'tour-metric-ingresosBonos',
    },
  ];

  const eur = (v: number) =>
    `${v.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €`;
  const comunes = {
    onNavigate: navigate,
    forzarAbierta: forzarAbiertas,
    linkLabel: (destino: string) => t('dashboardMetrics.irA', { destino }),
  };

  return (
    <div className="space-y-6">
      <Seccion
        id="finanzas"
        titulo={t('dashboardMetrics.seccionFinanzas')}
        resumen={`${t('dashboardMetrics.beneficioNeto')} ${eur(stats.beneficioMes)}`}
        grid="grid-cols-2 sm:grid-cols-3"
        metricas={finanzas}
        destacado
        {...comunes}
      />
      <Seccion
        id="actividad"
        titulo={t('dashboardMetrics.seccionActividad')}
        resumen={`${stats.citasMes} ${t('dashboardCharts.appointments')}`}
        grid="grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"
        metricas={actividad}
        {...comunes}
      />
      <Seccion
        id="bonos"
        titulo={t('dashboardMetrics.seccionBonos')}
        resumen={`${stats.bonosVendidosMes} ${t('dashboardMetrics.vendidos')}`}
        grid="grid-cols-2 lg:grid-cols-4"
        metricas={bonos}
        {...comunes}
      />
    </div>
  );
};

export default DashboardMetrics;
